import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import {
  Controller,
  FormProvider,
  type SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import type { z } from "zod";

import * as schemas from "@blis/shared/schemas/test-result-spec";
import { type RouterOutputs, trpc } from "@blis/trpc/react";

import { Link } from "@/components/Link";
import { Button } from "@/components/ui/button";
import { Checkbox, CheckboxField } from "@/components/ui/checkbox";
import {
  Description,
  FieldError,
  Input,
  Label,
  TextField,
} from "@/components/ui/field";
import {
  Select,
  SelectItem,
  SelectList,
  SelectSection,
  SelectTrigger,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { mutationResponse } from "@/utils/trpc";
import { AdminForm } from "../AdminForm";
import { Section } from "../Section";
import { TabLayout } from "../TabLayout";

type TestResultSpec = NonNullable<
  RouterOutputs["admin"]["testResultSpec"]["get"]
>;

type FormValues = z.infer<typeof schemas.create>;

export function ResultSpecDetailForm({
  onFetching,
  resultSpec,
  ...props
}: {
  resultSpec?: TestResultSpec;
  test: { id: number };
  hideFooter?: boolean;
  formId?: string;
  onComplete?: () => void;
  onFetching?: (fetching: boolean) => void;
}) {
  const update = trpc.admin.testResultSpec.update.useMutation();
  const create = trpc.admin.testResultSpec.create.useMutation();

  const methods = useForm({
    defaultValues: {
      ...resultSpec,
      name: resultSpec?.name || "",
      testId: props.test.id,
      rangeMin: resultSpec?.rangeMin?.toNumber(),
      rangeMax: resultSpec?.rangeMax?.toNumber(),
      percentChangeThreshold: resultSpec?.percentChangeThreshold?.toNumber(),
      absoluteChangeThreshold: resultSpec?.absoluteChangeThreshold?.toNumber(),
      options: (resultSpec?.options as object) || {},
    },
    resolver: zodResolver(schemas.create),
  });

  useEffect(() => {
    onFetching?.(create.isPending || update.isPending);
  }, [onFetching, create.isPending, update.isPending]);

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    if (resultSpec) {
      update.mutate(
        { id: resultSpec.id, patch: values },
        mutationResponse({
          errorTitle: "Error updating result spec",
          successMessage: "Test result spec updated",
          onSuccess() {
            props.onComplete?.();
          },
        }),
      );
    } else {
      create.mutate(
        values,
        mutationResponse({
          errorTitle: "Error creating test result spec",
          successMessage: "Test reuslt spec created",
          onSuccess() {
            props.onComplete?.();
          },
        }),
      );
    }
  };

  return (
    <TabLayout>
      <FormProvider {...methods}>
        <AdminForm
          onSubmit={methods.handleSubmit(onSubmit)}
          id={props.formId}
          className="gap-3"
        >
          <Controller
            control={methods.control}
            name="name"
            render={({ field, fieldState: { error } }) => (
              <TextField
                id="name"
                {...field}
                isInvalid={!!error}
                validationBehavior="aria"
              >
                <Label>Name</Label>
                <Input />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <ResultTypeSelect />
          <ResultComponentTypeSelect />
          <GenericOptions />
          <MaterialOptions />
          <TypeSpecificOptions />
          <ResultSpecOptions resultSpec={resultSpec} />
          {!props.hideFooter && (
            <>
              <Separator />
              <div className="flex justify-end items-center gap-2">
                <Link
                  to="/admin/tests/$id/edit"
                  params={{ id: props.test.id }}
                  variant="outline"
                  asButton
                >
                  Cancel
                </Link>
                <Button
                  type="submit"
                  isPending={update.isPending || create.isPending}
                >
                  Save
                </Button>
              </div>
            </>
          )}
        </AdminForm>
      </FormProvider>
    </TabLayout>
  );
}

const typeOptions = [
  { label: "Numeric", value: "NUMERIC" },
  { label: "Text", value: "TEXT" },
  { label: "HLA", value: "HLA" },
  { label: "Free text", value: "FREE_TEXT" },
  { label: "Organism", value: "ORGANISM" },
];

function ResultTypeSelect() {
  const { control, setValue } = useFormContext<FormValues>();
  return (
    <Controller
      control={control}
      name="type"
      render={({ field, fieldState: { error } }) => (
        <Select
          {...field}
          selectedKey={field.value}
          onSelectionChange={(key) => {
            field.onChange(key);
            // The qualitative delta checkbox only renders for TEXT specs, so
            // clear it on switch away to avoid submitting a stale hidden flag.
            if (key !== "TEXT") {
              setValue("flagOnQualitativeChange", false);
            }
          }}
          isInvalid={!!error}
          validationBehavior="aria"
        >
          <Label>Type</Label>
          <SelectTrigger />
          <SelectList items={typeOptions}>
            {(item) => <SelectItem id={item.value}>{item.label}</SelectItem>}
          </SelectList>
          <FieldError>{error?.message}</FieldError>
        </Select>
      )}
    />
  );
}

const componentTypeOptions = [
  { label: "Result", value: "RESULT" },
  { label: "Observation", value: "OBSERVATION" },
];

function ResultComponentTypeSelect() {
  const { control } = useFormContext<FormValues>();
  return (
    <Controller
      control={control}
      name="componentType"
      render={({ field, fieldState: { error } }) => (
        <Select
          {...field}
          selectedKey={field.value}
          onSelectionChange={field.onChange}
          isInvalid={!!error}
          validationBehavior="aria"
        >
          <Label>Component type</Label>
          <SelectTrigger />
          <SelectList items={componentTypeOptions}>
            {(item) => <SelectItem id={item.value}>{item.label}</SelectItem>}
          </SelectList>
          <FieldError>{error?.message}</FieldError>
        </Select>
      )}
    />
  );
}

function TypeSpecificOptions() {
  const { watch } = useFormContext<FormValues>();
  const type = watch("type");

  if (type === "NUMERIC") {
    return (
      <>
        <NumericOptions />
        <DeltaCheckFields />
      </>
    );
  }

  if (type === "TEXT") {
    return <QualitativeDeltaCheckField />;
  }

  if (type === "HLA") {
    return <HLAOptions />;
  }

  return null;
}

function QualitativeDeltaCheckField() {
  const { control } = useFormContext<FormValues>();
  return (
    <div className="flex flex-col gap-1">
      <Section.Subtitle>Delta checks</Section.Subtitle>
      <Controller
        control={control}
        name="flagOnQualitativeChange"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <CheckboxField>
            <Checkbox
              id="flagOnQualitativeChange"
              isSelected={field.value || false}
              onChange={(val) => field.onChange(!!val)}
              onBlur={field.onBlur}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              Flag when the result changes
            </Checkbox>
            <Description>
              Flag a delta when this result differs from the donor's most recent
              prior result for this result spec.
            </Description>
          </CheckboxField>
        )}
      />
    </div>
  );
}

function GenericOptions() {
  const { control } = useFormContext<FormValues>();

  return (
    <>
      <Controller
        control={control}
        name="units"
        render={({ field, fieldState: { error } }) => (
          <TextField
            id="units"
            {...field}
            value={field.value || ""}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Units</Label>
            <Input />
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />
      <Controller
        control={control}
        name="instrumentCode"
        render={({ field, fieldState: { error } }) => (
          <TextField
            id="instrumentCode"
            {...field}
            value={field.value || ""}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Instrument code</Label>
            <Input />
            <Description>
              The code used by the instrument to identify this result spec (i.e.
              HGB, LY%)
            </Description>
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />
    </>
  );
}

function NumericOptions() {
  const { control } = useFormContext<FormValues>();
  return (
    <div className="flex flex-col gap-1">
      <Section.Subtitle>Reportable range</Section.Subtitle>
      <div className="grid grid-cols-2 gap-2">
        <Controller
          control={control}
          name="rangeMin"
          render={({ field, fieldState: { error } }) => (
            <TextField
              type="number"
              id="rangeMin"
              {...field}
              value={field.value?.toString() || ""}
              onChange={(e) => field.onChange(e)}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <Label>Reportable range min</Label>
              <Input />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
        <Controller
          control={control}
          name="rangeMax"
          render={({ field, fieldState: { error } }) => (
            <TextField
              type="number"
              id="rangeMax"
              {...field}
              value={field.value?.toString() || ""}
              onChange={(e) => field.onChange(e)}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <Label>Reportable range max</Label>
              <Input />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
        <Controller
          control={control}
          name="rangeMinInclusive"
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <Checkbox
              id="rangeMinInclusive"
              isSelected={field.value || false}
              onChange={(val) => field.onChange(!!val)}
              onBlur={field.onBlur}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              Inclusive
            </Checkbox>
          )}
        />
        <Controller
          control={control}
          name="rangeMaxInclusive"
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <Checkbox
              id="rangeMinInclusive"
              isSelected={field.value || false}
              onChange={(val) => field.onChange(!!val)}
              onBlur={field.onBlur}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              Inclusive
            </Checkbox>
          )}
        />
      </div>
    </div>
  );
}

function DeltaCheckFields() {
  const { control } = useFormContext<FormValues>();
  return (
    <div className="flex flex-col gap-1">
      <Section.Subtitle>Delta checks</Section.Subtitle>
      <div className="grid grid-cols-2 gap-2">
        <Controller
          control={control}
          name="percentChangeThreshold"
          render={({ field, fieldState: { error } }) => (
            <TextField
              type="number"
              id="percentChangeThreshold"
              {...field}
              value={field.value?.toString() || ""}
              onChange={(e) => field.onChange(e || null)}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <Label>Percent change threshold</Label>
              <Input />
              <Description>
                If the percent change between two results is greater than this
                threshold, the result will be flagged as a delta.
              </Description>
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
        <Controller
          control={control}
          name="absoluteChangeThreshold"
          render={({ field, fieldState: { error } }) => (
            <TextField
              type="number"
              id="absoluteChangeThreshold"
              {...field}
              value={field.value?.toString() || ""}
              onChange={(e) => field.onChange(e || null)}
              isInvalid={!!error}
              validationBehavior="aria"
            >
              <Label>Absolute change threshold</Label>
              <Input />
              <Description>
                If the absolute change between two results is greater than this
                threshold, the result will be flagged as a delta.
              </Description>
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
      </div>
    </div>
  );
}

function MaterialOptions() {
  const { control } = useFormContext<FormValues>();
  const { data } = trpc.admin.materialSpec.list.useQuery();
  const materialSpecs = data || [];

  return (
    <Controller
      control={control}
      name="materialSpecId"
      render={({ field, fieldState: { error } }) => (
        <Select
          selectedKey={field.value}
          onSelectionChange={field.onChange}
          isInvalid={!!error}
          validationBehavior="aria"
          onBlur={field.onBlur}
        >
          <Label>Material Spec</Label>
          <SelectTrigger />

          <SelectList items={materialSpecs}>
            {(item) => (
              <SelectItem id={item.id} textValue={item.name}>
                {item.name}
              </SelectItem>
            )}
          </SelectList>
          <Description>
            Select the material that is used for this result spec.
          </Description>
          <FieldError>{error?.message}</FieldError>
        </Select>
      )}
    />
  );
}
function HLAOptions() {
  const { control } = useFormContext<FormValues>();
  const { data } = trpc.admin.testResultSpec.hla.classifications.useQuery();

  const options: {
    label: string;
    options: { label: string; value: string }[];
  }[] = [];

  for (const classification of data || []) {
    const existing = options.find((o) => o.label === classification.group);
    if (existing) {
      existing.options.push({
        label: classification.name,
        value: classification.name,
      });
    } else {
      options.push({
        label: classification.group,
        options: [
          {
            label: classification.name,
            value: classification.name,
          },
        ],
      });
    }
  }

  return (
    <Controller
      control={control}
      name="hlaClassification"
      render={({ field, fieldState: { error } }) => (
        <Select
          {...field}
          selectedKey={field.value}
          onSelectionChange={field.onChange}
          isInvalid={!!error}
          validationBehavior="aria"
        >
          <Label>HLA Classification</Label>
          <SelectTrigger />
          <SelectList items={options}>
            {(group) => (
              <SelectSection
                title={group.label}
                id={group.label}
                items={group.options}
              >
                {(option) => (
                  <SelectItem key={option.value} id={option.value}>
                    {option.label}
                  </SelectItem>
                )}
              </SelectSection>
            )}
          </SelectList>
          <FieldError>{error?.message}</FieldError>
        </Select>
      )}
    />
  );
}

function ResultSpecOptions({ resultSpec }: { resultSpec?: TestResultSpec }) {
  const { control, watch } = useFormContext<FormValues>();

  const isCalculated = watch("isCalculated", resultSpec?.isCalculated);

  return (
    <>
      <div className="flex flex-col gap-1">
        <Section.Subtitle>Options</Section.Subtitle>
        <Controller
          control={control}
          name="disableDoubleEntry"
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <CheckboxField>
              <Checkbox
                id="check-disable-double-entry"
                isSelected={field.value}
                onChange={(val) => field.onChange(!!val)}
                onBlur={field.onBlur}
                isInvalid={!!error}
                validationBehavior="aria"
              >
                Disable double entry
              </Checkbox>
              <Description>
                This result spec does not require double entry
              </Description>
            </CheckboxField>
          )}
        />
        <Controller
          control={control}
          name="isCalculated"
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <CheckboxField>
              <Checkbox
                id="check-is-calculated"
                isSelected={field.value}
                onChange={(val) => field.onChange(!!val)}
                onBlur={field.onBlur}
                isInvalid={!!error}
                validationBehavior="aria"
              >
                Is calculated
              </Checkbox>
              <Description>
                This result is calculated from another result
              </Description>
            </CheckboxField>
          )}
        />
      </div>
      {isCalculated && <CalculationOptions resultSpec={resultSpec} />}
    </>
  );
}

const calculationMethodOptions = [
  { label: "eGFR for Creatinine", value: "EGFR" },
];

function CalculationOptions({ resultSpec }: { resultSpec?: TestResultSpec }) {
  const { control, getValues } = useFormContext<FormValues>();

  const testSpecId = getValues("testId");

  const { data, isLoading } = trpc.admin.testResultSpec.list.useQuery({
    testSpecId,
  });
  const resultSpecs = (data || [])
    .filter((spec) => spec.id !== resultSpec?.id)
    .map((spec) => ({
      label: spec.name,
      value: spec.id,
    }));

  return (
    <div className="flex flex-col gap-1">
      <Section.Subtitle>Calculation options</Section.Subtitle>
      <Controller
        control={control}
        name="calculationMethod"
        render={({ field, fieldState: { error } }) => (
          <Select
            {...field}
            selectedKey={field.value}
            onSelectionChange={field.onChange}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Calculation method</Label>
            <SelectTrigger />
            <SelectList items={calculationMethodOptions}>
              {(item) => <SelectItem id={item.value}>{item.label}</SelectItem>}
            </SelectList>
            <FieldError>{error?.message}</FieldError>
          </Select>
        )}
      />
      <Controller
        control={control}
        name="calculationSourceId"
        render={({ field, fieldState: { error } }) => (
          <Select
            {...field}
            selectedKey={field.value}
            onSelectionChange={field.onChange}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Calculation source</Label>
            <SelectTrigger />
            <SelectList items={resultSpecs}>
              {(item) => <SelectItem id={item.value}>{item.label}</SelectItem>}
            </SelectList>
            <FieldError>{error?.message}</FieldError>
          </Select>
        )}
      />
    </div>
  );
}
